import { useEffect, useMemo, useRef, useState } from "react";
import {
  contextCards,
  cookingUnits,
  conversionGroups,
  convertValue,
  corePresets,
  formatNumber,
  getGroup,
  getUnit,
  ingredientProfiles,
  shippingDivisors
} from "./conversionData.js";
import { adsterraConfig } from "./adsterraConfig.js";

const ADSTERRA_BANNER_HOST = import.meta.env.VITE_ADSTERRA_BANNER_HOST || adsterraConfig.bannerHost;
const ADSTERRA_BANNERS = {
  "300x250": createAdsterraBanner(
    "300x250",
    300,
    250,
    import.meta.env.VITE_ADSTERRA_BANNER_300X250_KEY,
    import.meta.env.VITE_ADSTERRA_BANNER_300X250_SRC,
    adsterraConfig.banners["300x250"]
  ),
  "728x90": createAdsterraBanner(
    "728x90",
    728,
    90,
    import.meta.env.VITE_ADSTERRA_BANNER_728X90_KEY,
    import.meta.env.VITE_ADSTERRA_BANNER_728X90_SRC,
    adsterraConfig.banners["728x90"]
  ),
  "320x50": createAdsterraBanner(
    "320x50",
    320,
    50,
    import.meta.env.VITE_ADSTERRA_BANNER_320X50_KEY,
    import.meta.env.VITE_ADSTERRA_BANNER_320X50_SRC,
    adsterraConfig.banners["320x50"]
  ),
  "468x60": createAdsterraBanner(
    "468x60",
    468,
    60,
    import.meta.env.VITE_ADSTERRA_BANNER_468X60_KEY,
    import.meta.env.VITE_ADSTERRA_BANNER_468X60_SRC,
    adsterraConfig.banners["468x60"]
  ),
  "160x300": createAdsterraBanner(
    "160x300",
    160,
    300,
    import.meta.env.VITE_ADSTERRA_BANNER_160X300_KEY,
    import.meta.env.VITE_ADSTERRA_BANNER_160X300_SRC,
    adsterraConfig.banners["160x300"]
  ),
  "160x600": createAdsterraBanner(
    "160x600",
    160,
    600,
    import.meta.env.VITE_ADSTERRA_BANNER_160X600_KEY,
    import.meta.env.VITE_ADSTERRA_BANNER_160X600_SRC,
    adsterraConfig.banners["160x600"]
  )
};
const AD_PLACEMENTS = {
  top: ["728x90", "468x60", "320x50"],
  mid: ["728x90", "468x60", "320x50"],
  side: ["300x250"],
  leftRail: ["160x600"],
  rightRail: ["160x600"]
};
const ADSTERRA_NATIVE = {
  key: import.meta.env.VITE_ADSTERRA_NATIVE_KEY || adsterraConfig.native.key,
  host: import.meta.env.VITE_ADSTERRA_NATIVE_HOST || adsterraConfig.native.host,
  scriptSrc: import.meta.env.VITE_ADSTERRA_NATIVE_SRC || adsterraConfig.native.scriptSrc,
  containerId: import.meta.env.VITE_ADSTERRA_NATIVE_CONTAINER_ID || adsterraConfig.native.containerId
};
const ADSTERRA_POPUNDER = {
  key: import.meta.env.VITE_ADSTERRA_POPUNDER_KEY || adsterraConfig.popunder.key,
  host: import.meta.env.VITE_ADSTERRA_POPUNDER_HOST || adsterraConfig.popunder.host,
  scriptSrc: import.meta.env.VITE_ADSTERRA_POPUNDER_SRC || adsterraConfig.popunder.scriptSrc
};
let adsterraBannerQueue = Promise.resolve();

function createAdsterraBanner(size, width, height, key = "", scriptSrc = "", fallback = {}) {
  return {
    size,
    width,
    height,
    key: key || fallback.key || "",
    scriptSrc: scriptSrc || fallback.scriptSrc || ""
  };
}

function normalizeScriptSrc(src) {
  const trimmed = src.trim();
  if (!trimmed) {
    return "";
  }
  if (trimmed.startsWith("//")) {
    return `https:${trimmed}`;
  }
  if (/^https?:\/\//i.test(trimmed)) {
    return trimmed;
  }
  return `https://${trimmed.replace(/^\/+/, "")}`;
}

function normalizeHost(host) {
  return host.trim().replace(/^https?:\/\//i, "").replace(/^\/\//, "").replace(/\/+$/, "");
}

function getAdsterraInvokeSrc({ key, host, scriptSrc }) {
  if (scriptSrc) {
    return normalizeScriptSrc(scriptSrc);
  }

  const trimmedKey = key.trim();
  if (/^(https?:)?\/\//i.test(trimmedKey)) {
    return normalizeScriptSrc(trimmedKey);
  }

  const normalizedHost = normalizeHost(host);
  if (!normalizedHost || !trimmedKey) {
    return "";
  }

  return `https://${normalizedHost}/${trimmedKey}/invoke.js`;
}

function getAdsterraBannerKey({ key, scriptSrc }) {
  const trimmedKey = key.trim();
  if (trimmedKey) {
    return trimmedKey;
  }

  const match = normalizeScriptSrc(scriptSrc).match(/\/([^/]+)\/invoke\.js(?:[?#].*)?$/i);
  return match ? match[1] : "";
}

function parseNumber(value, fallback = 0) {
  const number = Number(value);
  return Number.isFinite(number) ? number : fallback;
}

function unitLabel(groupId, unitId) {
  const group = getGroup(groupId);
  return getUnit(group, unitId).short;
}

function lengthToMeters(value, unit) {
  const number = parseNumber(value);
  const factors = {
    ft: 0.3048,
    in: 0.0254,
    yd: 0.9144,
    m: 1,
    cm: 0.01
  };
  return number * (factors[unit] || 1);
}

function formatMeasurement(value, unit) {
  return `${formatNumber(value)} ${unit}`;
}

function slugify(text) {
  return text
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "");
}

function conversionPath(preset) {
  return `/convert/${slugify(preset.title)}`;
}

function toolPath(context) {
  return `/tools/${slugify(context.title)}`;
}

function getPathname() {
  return window.location.pathname.replace(/\/+$/, "") || "/";
}

function usePathname() {
  const [pathname, setPathname] = useState(getPathname);

  useEffect(() => {
    const updatePath = () => setPathname(getPathname());
    window.addEventListener("popstate", updatePath);
    return () => window.removeEventListener("popstate", updatePath);
  }, []);

  return pathname;
}

function usePageMeta(title, description) {
  useEffect(() => {
    document.title = title === "Convert Everything" ? title : `${title} - Convert Everything`;

    let descriptionMeta = document.querySelector('meta[name="description"]');
    if (!descriptionMeta) {
      descriptionMeta = document.createElement("meta");
      descriptionMeta.setAttribute("name", "description");
      document.head.appendChild(descriptionMeta);
    }
    descriptionMeta.setAttribute("content", description);

    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.setAttribute("rel", "canonical");
      document.head.appendChild(canonical);
    }
    canonical.setAttribute("href", `${window.location.origin}${getPathname()}`);
  }, [description, title]);
}

function App() {
  const pathname = usePathname();
  const route = resolveRoute(pathname);

  return (
    <>
      <AdsterraPopunder />
      <GlobalRailAds />
      <div className="app-shell">
        <header className="site-header">
          <a className="brand" href="/" aria-label="Convert Everything home">
            <span className="brand-mark">CE</span>
            <span>Convert Everything</span>
          </a>
          <nav className="header-nav" aria-label="Primary">
            <a className={pathname === "/" ? "active" : ""} href="/">Home</a>
            <a className={pathname.startsWith("/convert") || pathname === "/conversions" ? "active" : ""} href="/conversions">Conversions</a>
            <a className={pathname.startsWith("/tools") ? "active" : ""} href="/tools">Tools</a>
            <a className={pathname === "/formulas" ? "active" : ""} href="/formulas">Formulas</a>
          </nav>
        </header>

        <main>
          {route.type === "home" ? <HomePage /> : null}
          {route.type === "conversions" ? <ConversionsIndexPage /> : null}
          {route.type === "conversion" ? <ConversionDetailPage preset={route.preset} /> : null}
          {route.type === "tools" ? <ToolsIndexPage /> : null}
          {route.type === "tool" ? <ToolDetailPage context={route.context} /> : null}
          {route.type === "formulas" ? <FormulasPage /> : null}
          {route.type === "not-found" ? <NotFoundPage /> : null}
          <AdsterraNativeAd className="native-ad" />
        </main>

        <footer className="site-footer">
          <p>Convert Everything runs fully in the browser and is ready for Cloudflare Pages.</p>
        </footer>
      </div>
    </>
  );
}

function GlobalRailAds() {
  return (
    <>
      <AdsterraBannerAd placement={AD_PLACEMENTS.leftRail} className="rail-ad rail-ad-left" />
      <AdsterraBannerAd placement={AD_PLACEMENTS.rightRail} className="rail-ad rail-ad-right" />
    </>
  );
}

function resolveRoute(pathname) {
  if (pathname === "/") {
    return { type: "home" };
  }
  if (pathname === "/conversions") {
    return { type: "conversions" };
  }
  if (pathname === "/tools") {
    return { type: "tools" };
  }
  if (pathname === "/formulas") {
    return { type: "formulas" };
  }

  const conversionMatch = pathname.match(/^\/convert\/([^/]+)$/);
  if (conversionMatch) {
    const preset = corePresets.find((item) => slugify(item.title) === conversionMatch[1]);
    return preset ? { type: "conversion", preset } : { type: "not-found" };
  }

  const toolMatch = pathname.match(/^\/tools\/([^/]+)$/);
  if (toolMatch) {
    const context = contextCards.find((item) => slugify(item.title) === toolMatch[1]);
    return context ? { type: "tool", context } : { type: "not-found" };
  }

  return { type: "not-found" };
}

function HomePage() {
  usePageMeta(
    "Convert Everything",
    "Fast unit conversions and practical calculators for room size, cooking measurements, tire pressure, fabric, shipping weight, fuel economy, and more."
  );

  return (
    <>
      <section className="tool-stage" aria-labelledby="page-title">
        <div className="calculator-panel">
          <div className="panel-heading">
            <p className="eyebrow">Browser-only unit converter</p>
            <h1 id="page-title">Convert Everything</h1>
            <p>
              Fast conversions for cm to inches, kg to lbs, Celsius to Fahrenheit, sq ft to sq m,
              liters to gallons, kPa to psi, mph to km/h, and practical calculators for real jobs.
            </p>
          </div>
          <ConversionCalculator preset={corePresets[0]} />
        </div>

        <aside className="stage-side">
          <img src="/measure-tools.svg" alt="Ruler, scale, and measuring cup illustration" />
          <div className="context-strip" aria-label="Featured context tools">
            {contextCards.slice(0, 5).map((context) => (
              <a key={context.id} href={toolPath(context)}>
                <span>{context.eyebrow}</span>
                {context.title}
              </a>
            ))}
          </div>
        </aside>
      </section>

      <AdsterraBannerAd placement={AD_PLACEMENTS.top} className="wide-ad" />

      <section className="content-section" aria-labelledby="home-conversions-title">
        <div className="section-heading">
          <div>
            <p className="eyebrow">Popular unit pages</p>
            <h2 id="home-conversions-title">Quick conversions</h2>
          </div>
          <a className="text-link" href="/conversions">View all</a>
        </div>
        <div className="preset-grid">
          {corePresets.slice(0, 12).map((preset) => (
            <PresetCard key={preset.id} preset={preset} />
          ))}
        </div>
      </section>

      <section className="content-section" aria-labelledby="home-tools-title">
        <div className="section-heading">
          <div>
            <p className="eyebrow">Use-case pages</p>
            <h2 id="home-tools-title">Context calculators</h2>
          </div>
          <a className="text-link" href="/tools">View all</a>
        </div>
        <div className="tool-grid">
          {contextCards.slice(0, 8).map((context) => (
            <ContextCard key={context.id} context={context} />
          ))}
        </div>
      </section>
    </>
  );
}

function ConversionsIndexPage() {
  const [query, setQuery] = useState("");
  const [activeGroup, setActiveGroup] = useState("all");

  usePageMeta(
    "All Unit Conversions",
    "Browse conversion pages for length, weight, temperature, area, volume, pressure, speed, fuel economy, digital storage, energy, power, time, and running pace."
  );

  const filteredPresets = useMemo(() => {
    const search = query.trim().toLowerCase();
    return corePresets.filter((preset) => {
      const matchesGroup = activeGroup === "all" || preset.group === activeGroup;
      const haystack = [preset.title, preset.formula, preset.group, ...(preset.tags || [])].join(" ").toLowerCase();
      return matchesGroup && (!search || haystack.includes(search));
    });
  }, [activeGroup, query]);

  return (
    <>
      <PageHero
        eyebrow="Unit conversion pages"
        title="All conversions"
        description="Each common conversion now has its own page, calculator, formula, related links, and ad placement."
      />

      <section className="search-row" aria-label="Search conversions">
        <label>
          <span>Find a converter</span>
          <input
            type="search"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Try kg, Celsius, square feet, gallons, psi, mpg..."
          />
        </label>
      </section>

      <div className="group-tabs page-tabs" role="tablist" aria-label="Conversion categories">
        <button className={activeGroup === "all" ? "active" : ""} type="button" onClick={() => setActiveGroup("all")}>
          All
        </button>
        {conversionGroups.map((group) => (
          <button
            className={group.id === activeGroup ? "active" : ""}
            key={group.id}
            type="button"
            onClick={() => setActiveGroup(group.id)}
          >
            {group.label}
          </button>
        ))}
      </div>

      <section className="content-section">
        <div className="preset-grid">
          {filteredPresets.map((preset) => (
            <PresetCard key={preset.id} preset={preset} />
          ))}
        </div>
      </section>

      <AdsterraBannerAd placement={AD_PLACEMENTS.mid} className="wide-ad" />
    </>
  );
}

function ConversionDetailPage({ preset }) {
  const related = corePresets
    .filter((item) => item.id !== preset.id && (item.group === preset.group || item.tags.some((tag) => preset.tags.includes(tag))))
    .slice(0, 6);

  usePageMeta(
    titleCase(preset.title),
    `Convert ${preset.title} with a live calculator, formula, and related unit conversion pages.`
  );

  return (
    <>
      <PageHero
        eyebrow={`${getGroup(preset.group).label} conversion`}
        title={titleCase(preset.title)}
        description={`Use this dedicated ${preset.title} calculator. Change the amount or switch units for nearby conversions.`}
      />

      <section className="detail-layout">
        <div className="calculator-panel">
          <ConversionCalculator preset={preset} />
        </div>
        <aside className="detail-side">
          <AdsterraBannerAd placement={AD_PLACEMENTS.side} className="side-ad" />
          <article className="mini-panel">
            <p className="eyebrow">Formula</p>
            <h3>{preset.formula}</h3>
            <p>
              Default example: {formatMeasurement(preset.input, unitLabel(preset.group, preset.from))} ={" "}
              {formatMeasurement(convertValue(preset.group, preset.from, preset.to, preset.input), unitLabel(preset.group, preset.to))}.
            </p>
          </article>
        </aside>
      </section>

      <section className="content-section">
        <div className="section-heading">
          <div>
            <p className="eyebrow">Related pages</p>
            <h2>More converters</h2>
          </div>
          <a className="text-link" href="/conversions">All conversions</a>
        </div>
        <div className="preset-grid">
          {related.map((item) => (
            <PresetCard key={item.id} preset={item} />
          ))}
        </div>
      </section>
    </>
  );
}

function ToolsIndexPage() {
  const [query, setQuery] = useState("");

  usePageMeta(
    "Context Calculators",
    "Practical calculators for room size, cooking measurements, tire pressure, fabric, shipping weight, fuel economy, paint coverage, pool volume, storage, HVAC, land area, and running pace."
  );

  const filteredContexts = useMemo(() => {
    const search = query.trim().toLowerCase();
    return contextCards.filter((context) => {
      const haystack = [context.title, context.eyebrow, context.description, ...(context.tags || [])].join(" ").toLowerCase();
      return !search || haystack.includes(search);
    });
  }, [query]);

  return (
    <>
      <PageHero
        eyebrow="Situation-specific calculators"
        title="Context tools"
        description="Pages for real tasks: rooms, recipes, tires, sewing, packages, fuel, paint, pools, storage, HVAC, land, and running."
      />

      <section className="search-row" aria-label="Search context tools">
        <label>
          <span>Find a tool</span>
          <input
            type="search"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Try cooking, tire, room, shipping, paint, pool..."
          />
        </label>
      </section>

      <section className="content-section">
        <div className="tool-grid">
          {filteredContexts.map((context) => (
            <ContextCard key={context.id} context={context} />
          ))}
        </div>
      </section>

      <AdsterraBannerAd placement={AD_PLACEMENTS.mid} className="wide-ad" />
    </>
  );
}

function ToolDetailPage({ context }) {
  const related = contextCards
    .filter((item) => item.id !== context.id && item.tags.some((tag) => context.tags.includes(tag)))
    .concat(contextCards.filter((item) => item.id !== context.id))
    .filter((item, index, list) => list.findIndex((candidate) => candidate.id === item.id) === index)
    .slice(0, 6);

  usePageMeta(context.title, `${context.description} Open the ${context.title} page with a dedicated browser-only calculator.`);

  return (
    <>
      <PageHero eyebrow={context.eyebrow} title={context.title} description={context.description} />

      <section className="detail-layout">
        <div className="context-workbench">
          <ContextTool context={context} />
        </div>
        <aside className="detail-side">
          <AdsterraBannerAd placement={AD_PLACEMENTS.side} className="side-ad" />
          <article className="mini-panel">
            <p className="eyebrow">Related unit converter</p>
            <h3>{titleCase(context.title)}</h3>
            <p>
              This page uses the same offline unit engine as the main converter, with inputs tuned for the selected task.
            </p>
          </article>
        </aside>
      </section>

      <section className="content-section">
        <div className="section-heading">
          <div>
            <p className="eyebrow">More tools</p>
            <h2>Related calculators</h2>
          </div>
          <a className="text-link" href="/tools">All tools</a>
        </div>
        <div className="tool-grid">
          {related.map((item) => (
            <ContextCard key={item.id} context={item} />
          ))}
        </div>
      </section>
    </>
  );
}

function FormulasPage() {
  usePageMeta(
    "Conversion Formulas",
    "Reference formulas for cm to inches, kg to lbs, Celsius to Fahrenheit, sq ft to sq m, liters to gallons, kPa to psi, mph to km/h, and more."
  );

  return (
    <>
      <PageHero
        eyebrow="Reference sheet"
        title="Conversion formulas"
        description="A compact formula page for the most common conversions in the app."
      />
      <section className="content-section formula-section">
        <div className="formula-grid">
          {corePresets.map((preset) => (
            <a className="formula-card" href={conversionPath(preset)} key={preset.id}>
              <h3>{preset.title}</h3>
              <p>{preset.formula}</p>
            </a>
          ))}
        </div>
      </section>
    </>
  );
}

function NotFoundPage() {
  usePageMeta(
    "Page Not Found",
    "The requested converter page was not found. Browse all unit conversions and context calculators."
  );

  return (
    <section className="content-section not-found">
      <p className="eyebrow">404</p>
      <h1>Page not found</h1>
      <p>The converter you opened does not exist yet.</p>
      <div className="action-row">
        <a className="primary-link" href="/conversions">Browse conversions</a>
        <a className="text-link" href="/tools">Browse tools</a>
      </div>
    </section>
  );
}

function PageHero({ eyebrow, title, description }) {
  return (
    <section className="page-hero">
      <p className="eyebrow">{eyebrow}</p>
      <h1>{title}</h1>
      <p>{description}</p>
    </section>
  );
}

function ConversionCalculator({ preset }) {
  const [activeGroupId, setActiveGroupId] = useState(preset.group);
  const activeGroup = getGroup(activeGroupId);
  const [fromUnit, setFromUnit] = useState(preset.from);
  const [toUnit, setToUnit] = useState(preset.to);
  const [inputValue, setInputValue] = useState(preset.input);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    setActiveGroupId(preset.group);
    setFromUnit(preset.from);
    setToUnit(preset.to);
    setInputValue(preset.input);
  }, [preset]);

  const from = getUnit(activeGroup, fromUnit);
  const to = getUnit(activeGroup, toUnit);
  const result = useMemo(
    () => convertValue(activeGroupId, fromUnit, toUnit, inputValue),
    [activeGroupId, fromUnit, inputValue, toUnit]
  );
  const formattedResult = formatNumber(result);

  function selectGroup(groupId) {
    const group = getGroup(groupId);
    setActiveGroupId(group.id);
    setFromUnit(group.units[0].id);
    setToUnit(group.units[1]?.id || group.units[0].id);
    setInputValue("1");
  }

  function swapUnits() {
    setFromUnit(toUnit);
    setToUnit(fromUnit);
  }

  async function copyResult() {
    const text = `${inputValue} ${from.short} = ${formattedResult} ${to.short}`;
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1400);
    } catch {
      setCopied(false);
    }
  }

  return (
    <div className="calculator-stack">
      <div className="group-tabs" role="tablist" aria-label="Conversion categories">
        {conversionGroups.map((group) => (
          <button
            className={group.id === activeGroupId ? "active" : ""}
            key={group.id}
            type="button"
            onClick={() => selectGroup(group.id)}
            role="tab"
            aria-selected={group.id === activeGroupId}
          >
            {group.label}
          </button>
        ))}
      </div>

      <div className="converter-grid">
        <label>
          <span>Amount</span>
          <input
            inputMode="decimal"
            type="number"
            value={inputValue}
            onChange={(event) => setInputValue(event.target.value)}
            aria-label="Amount to convert"
          />
        </label>

        <label>
          <span>From</span>
          <select value={fromUnit} onChange={(event) => setFromUnit(event.target.value)}>
            {activeGroup.units.map((unit) => (
              <option key={unit.id} value={unit.id}>
                {unit.label}
              </option>
            ))}
          </select>
        </label>

        <button className="swap-button" type="button" onClick={swapUnits} title="Swap units" aria-label="Swap units">
          &lt;-&gt;
        </button>

        <label>
          <span>To</span>
          <select value={toUnit} onChange={(event) => setToUnit(event.target.value)}>
            {activeGroup.units.map((unit) => (
              <option key={unit.id} value={unit.id}>
                {unit.label}
              </option>
            ))}
          </select>
        </label>
      </div>

      <div className="result-band" aria-live="polite">
        <span className="result-label">{from.label} to {to.label}</span>
        <strong>{formattedResult || "Enter a valid number"}</strong>
        {formattedResult ? <span>{to.short}</span> : null}
        <button type="button" onClick={copyResult} disabled={!formattedResult}>
          {copied ? "Copied" : "Copy"}
        </button>
      </div>
    </div>
  );
}

function PresetCard({ preset }) {
  return (
    <a className="preset-card" href={conversionPath(preset)}>
      <span>{preset.title}</span>
      <strong>
        {formatMeasurement(preset.input, unitLabel(preset.group, preset.from))} ={" "}
        {formatMeasurement(convertValue(preset.group, preset.from, preset.to, preset.input), unitLabel(preset.group, preset.to))}
      </strong>
      <small>{preset.formula}</small>
    </a>
  );
}

function ContextCard({ context }) {
  return (
    <a className="tool-card-link" href={toolPath(context)}>
      <span>{context.eyebrow}</span>
      <strong>{context.title}</strong>
      <small>{context.description}</small>
    </a>
  );
}

function titleCase(text) {
  return text.replace(/\b\w/g, (letter) => letter.toUpperCase());
}

function selectResponsiveAdSize(sizes) {
  const viewportWidth = typeof window === "undefined" ? 1180 : window.innerWidth;
  const maxWidth = viewportWidth <= 560 ? viewportWidth : Math.max(160, viewportWidth - 40);
  return sizes.find((size) => ADSTERRA_BANNERS[size]?.width <= maxWidth) || sizes[sizes.length - 1];
}

function useResponsiveAdSize(sizes) {
  const [size, setSize] = useState(() => selectResponsiveAdSize(sizes));
  const sizesKey = sizes.join("|");

  useEffect(() => {
    function handleResize() {
      setSize(selectResponsiveAdSize(sizes));
    }

    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [sizes, sizesKey]);

  return size;
}

function AdsterraBannerAd({ placement, className = "" }) {
  const size = useResponsiveAdSize(placement);
  const banner = ADSTERRA_BANNERS[size];
  const mountRef = useRef(null);
  const bannerKey = getAdsterraBannerKey(banner);
  const src = getAdsterraInvokeSrc({
    key: bannerKey,
    host: ADSTERRA_BANNER_HOST,
    scriptSrc: banner.scriptSrc
  });
  const hasAd = Boolean(bannerKey && src);

  useEffect(() => {
    if (!hasAd) {
      return undefined;
    }

    const node = mountRef.current;
    let active = true;

    const renderAd = () => new Promise((resolve) => {
      if (!active || !node || !node.isConnected) {
        resolve();
        return;
      }

      node.replaceChildren();
      window.atOptions = {
        key: bannerKey,
        format: "iframe",
        height: banner.height,
        width: banner.width,
        params: {}
      };

      const script = document.createElement("script");
      script.type = "text/javascript";
      script.async = false;
      script.src = src;

      const finish = () => {
        window.clearTimeout(timeout);
        resolve();
      };
      const timeout = window.setTimeout(finish, 2500);
      script.onload = finish;
      script.onerror = finish;
      node.appendChild(script);
    });

    adsterraBannerQueue = adsterraBannerQueue.then(renderAd, renderAd);

    return () => {
      active = false;
      if (node) {
        node.replaceChildren();
      }
    };
  }, [banner.height, banner.width, bannerKey, hasAd, src]);

  return (
    <aside
      className={`ad-slot adsterra-banner ${className}`}
      aria-label={`${banner.size} advertisement`}
      style={{ "--ad-width": `${banner.width}px`, "--ad-height": `${banner.height}px` }}
    >
      <span>Advertisement</span>
      <div className="adsterra-ad-frame" ref={mountRef}>
        {!hasAd ? <div className="ad-placeholder"><small>{banner.size}</small></div> : null}
      </div>
    </aside>
  );
}

function AdsterraNativeAd({ className = "" }) {
  const mountRef = useRef(null);
  const src = getAdsterraInvokeSrc(ADSTERRA_NATIVE);
  const containerId = ADSTERRA_NATIVE.containerId || (ADSTERRA_NATIVE.key ? `container-${ADSTERRA_NATIVE.key}` : "");
  const hasAd = Boolean(src && containerId);

  useEffect(() => {
    if (!hasAd) {
      return undefined;
    }

    const node = mountRef.current;
    if (!node) {
      return undefined;
    }

    node.replaceChildren();

    const container = document.createElement("div");
    container.id = containerId;
    node.appendChild(container);

    const script = document.createElement("script");
    script.async = true;
    script.setAttribute("data-cfasync", "false");
    script.src = src;
    node.appendChild(script);

    return () => {
      node.replaceChildren();
    };
  }, [containerId, hasAd, src]);

  return (
    <aside className={`ad-slot adsterra-native ${className}`} aria-label="Native advertisement">
      <span>Native Advertisement</span>
      <div className="adsterra-native-frame" ref={mountRef}>
        {!hasAd ? <div className="ad-placeholder"><small>Native Banner</small></div> : null}
      </div>
    </aside>
  );
}

function AdsterraPopunder() {
  const src = getAdsterraInvokeSrc(ADSTERRA_POPUNDER);

  useEffect(() => {
    if (!src || document.getElementById("adsterra-popunder-script")) {
      return undefined;
    }

    const script = document.createElement("script");
    script.id = "adsterra-popunder-script";
    script.async = true;
    script.setAttribute("data-cfasync", "false");
    script.src = src;
    document.head.appendChild(script);

    return () => {
      script.remove();
    };
  }, [src]);

  return null;
}

function ContextTool({ context }) {
  if (context.id === "room") {
    return <RoomSizeConverter context={context} />;
  }
  if (context.id === "cooking") {
    return <CookingConverter context={context} />;
  }
  if (context.id === "tire") {
    return <TirePressureConverter context={context} />;
  }
  if (context.id === "fabric") {
    return <FabricConverter context={context} />;
  }
  if (context.id === "shipping") {
    return <ShippingConverter context={context} />;
  }
  if (context.id === "paint") {
    return <PaintCoverageConverter context={context} />;
  }
  if (context.id === "pool") {
    return <PoolVolumeConverter context={context} />;
  }
  return <GenericContextConverter context={context} />;
}

function ToolHeader({ context }) {
  return (
    <div className="tool-header">
      <p className="eyebrow">{context.eyebrow}</p>
      <h3>{context.title}</h3>
      <p>{context.description}</p>
    </div>
  );
}

function StatGrid({ items }) {
  return (
    <div className="stat-grid">
      {items.map((item) => (
        <div className="stat" key={item.label}>
          <span>{item.label}</span>
          <strong>{item.value}</strong>
          {item.note ? <small>{item.note}</small> : null}
        </div>
      ))}
    </div>
  );
}

function RoomSizeConverter({ context }) {
  const [length, setLength] = useState("12");
  const [width, setWidth] = useState("10");
  const [unit, setUnit] = useState("ft");
  const [overage, setOverage] = useState("10");
  const lengthMeters = lengthToMeters(length, unit);
  const widthMeters = lengthToMeters(width, unit);
  const areaSqm = lengthMeters * widthMeters;
  const areaSqft = areaSqm / 0.09290304;
  const overageFactor = 1 + parseNumber(overage) / 100;

  return (
    <div className="tool-card">
      <ToolHeader context={context} />
      <div className="form-grid four">
        <label>
          <span>Length</span>
          <input type="number" value={length} onChange={(event) => setLength(event.target.value)} />
        </label>
        <label>
          <span>Width</span>
          <input type="number" value={width} onChange={(event) => setWidth(event.target.value)} />
        </label>
        <label>
          <span>Unit</span>
          <select value={unit} onChange={(event) => setUnit(event.target.value)}>
            <option value="ft">feet</option>
            <option value="m">meters</option>
            <option value="in">inches</option>
            <option value="cm">centimeters</option>
          </select>
        </label>
        <label>
          <span>Overage</span>
          <input type="number" value={overage} onChange={(event) => setOverage(event.target.value)} />
        </label>
      </div>
      <StatGrid
        items={[
          { label: "Square feet", value: formatMeasurement(areaSqft, "sq ft") },
          { label: "Square meters", value: formatMeasurement(areaSqm, "sq m") },
          { label: "Pyeong", value: formatMeasurement(areaSqm / 3.305785124, "pyeong") },
          { label: "Flooring order", value: formatMeasurement(areaSqft * overageFactor, "sq ft"), note: `${formatNumber(parseNumber(overage))}% overage` }
        ]}
      />
    </div>
  );
}

function CookingConverter({ context }) {
  const [amount, setAmount] = useState("1");
  const [fromUnit, setFromUnit] = useState("cup");
  const [toUnit, setToUnit] = useState("g");
  const [ingredientId, setIngredientId] = useState("flour");
  const ingredient = ingredientProfiles.find((item) => item.id === ingredientId) || ingredientProfiles[0];
  const from = cookingUnits.find((unit) => unit.id === fromUnit) || cookingUnits[0];
  const to = cookingUnits.find((unit) => unit.id === toUnit) || cookingUnits[0];
  const grams = toGrams(parseNumber(amount), from, ingredient.density);
  const result = fromGrams(grams, to, ingredient.density);
  const ml = grams / ingredient.density;

  return (
    <div className="tool-card">
      <ToolHeader context={context} />
      <div className="form-grid four">
        <label>
          <span>Amount</span>
          <input type="number" value={amount} onChange={(event) => setAmount(event.target.value)} />
        </label>
        <label>
          <span>From</span>
          <select value={fromUnit} onChange={(event) => setFromUnit(event.target.value)}>
            {cookingUnits.map((unit) => (
              <option key={unit.id} value={unit.id}>
                {unit.label}
              </option>
            ))}
          </select>
        </label>
        <label>
          <span>To</span>
          <select value={toUnit} onChange={(event) => setToUnit(event.target.value)}>
            {cookingUnits.map((unit) => (
              <option key={unit.id} value={unit.id}>
                {unit.label}
              </option>
            ))}
          </select>
        </label>
        <label>
          <span>Ingredient</span>
          <select value={ingredientId} onChange={(event) => setIngredientId(event.target.value)}>
            {ingredientProfiles.map((item) => (
              <option key={item.id} value={item.id}>
                {item.label}
              </option>
            ))}
          </select>
        </label>
      </div>
      <StatGrid
        items={[
          { label: "Converted amount", value: formatMeasurement(result, to.label) },
          { label: "Weight", value: formatMeasurement(grams, "g") },
          { label: "Volume", value: formatMeasurement(ml, "ml") },
          { label: "Density used", value: `${formatNumber(ingredient.density)} g/ml`, note: ingredient.label }
        ]}
      />
    </div>
  );
}

function TirePressureConverter({ context }) {
  const [pressure, setPressure] = useState("35");
  const [unit, setUnit] = useState("psi");
  const psi = convertValue("pressure", unit, "psi", pressure);
  const kpa = convertValue("pressure", unit, "kpa", pressure);
  const bar = convertValue("pressure", unit, "bar", pressure);

  return (
    <div className="tool-card">
      <ToolHeader context={context} />
      <div className="form-grid two">
        <label>
          <span>Pressure</span>
          <input type="number" value={pressure} onChange={(event) => setPressure(event.target.value)} />
        </label>
        <label>
          <span>Unit</span>
          <select value={unit} onChange={(event) => setUnit(event.target.value)}>
            <option value="psi">psi</option>
            <option value="kpa">kPa</option>
            <option value="bar">bar</option>
          </select>
        </label>
      </div>
      <StatGrid
        items={[
          { label: "PSI", value: formatMeasurement(psi, "psi") },
          { label: "kPa", value: formatMeasurement(kpa, "kPa") },
          { label: "Bar", value: formatMeasurement(bar, "bar") },
          { label: "Typical car range", value: "30 to 35 psi", note: "Check the door placard for your vehicle" }
        ]}
      />
    </div>
  );
}

function FabricConverter({ context }) {
  const [length, setLength] = useState("3");
  const [width, setWidth] = useState("45");
  const [lengthUnit, setLengthUnit] = useState("yd");
  const [widthUnit, setWidthUnit] = useState("in");
  const lengthMeters = lengthToMeters(length, lengthUnit);
  const widthMeters = lengthToMeters(width, widthUnit);
  const areaSqm = lengthMeters * widthMeters;
  const areaSqyd = areaSqm / 0.83612736;

  return (
    <div className="tool-card">
      <ToolHeader context={context} />
      <div className="form-grid four">
        <label>
          <span>Length</span>
          <input type="number" value={length} onChange={(event) => setLength(event.target.value)} />
        </label>
        <label>
          <span>Length unit</span>
          <select value={lengthUnit} onChange={(event) => setLengthUnit(event.target.value)}>
            <option value="yd">yards</option>
            <option value="m">meters</option>
            <option value="ft">feet</option>
          </select>
        </label>
        <label>
          <span>Bolt width</span>
          <input type="number" value={width} onChange={(event) => setWidth(event.target.value)} />
        </label>
        <label>
          <span>Width unit</span>
          <select value={widthUnit} onChange={(event) => setWidthUnit(event.target.value)}>
            <option value="in">inches</option>
            <option value="cm">centimeters</option>
            <option value="m">meters</option>
          </select>
        </label>
      </div>
      <StatGrid
        items={[
          { label: "Length", value: `${formatMeasurement(lengthMeters, "m")} / ${formatMeasurement(lengthMeters / 0.9144, "yd")}` },
          { label: "Width", value: `${formatMeasurement(widthMeters * 100, "cm")} / ${formatMeasurement(widthMeters / 0.0254, "in")}` },
          { label: "Area", value: formatMeasurement(areaSqm, "sq m") },
          { label: "Area in square yards", value: formatMeasurement(areaSqyd, "sq yd") }
        ]}
      />
    </div>
  );
}

function ShippingConverter({ context }) {
  const [system, setSystem] = useState("imperial");
  const [weight, setWeight] = useState("8");
  const [length, setLength] = useState("18");
  const [width, setWidth] = useState("12");
  const [height, setHeight] = useState("10");
  const [divisorId, setDivisorId] = useState("139");

  const divisors = shippingDivisors.filter((divisor) => divisor.system === system);
  const divisor = divisors.find((item) => item.id === divisorId) || divisors[0];

  useEffect(() => {
    setDivisorId(system === "imperial" ? "139" : "5000");
  }, [system]);

  const volume = parseNumber(length) * parseNumber(width) * parseNumber(height);
  const dimensional = volume / divisor.divisor;
  const actual = parseNumber(weight);
  const billable = Math.max(actual, dimensional);
  const billableKg = system === "imperial" ? billable * 0.45359237 : billable;
  const billableLb = system === "metric" ? billable * 2.2046226218 : billable;

  return (
    <div className="tool-card">
      <ToolHeader context={context} />
      <div className="form-grid four">
        <label>
          <span>System</span>
          <select value={system} onChange={(event) => setSystem(event.target.value)}>
            <option value="imperial">lb and inches</option>
            <option value="metric">kg and cm</option>
          </select>
        </label>
        <label>
          <span>Actual weight</span>
          <input type="number" value={weight} onChange={(event) => setWeight(event.target.value)} />
        </label>
        <label>
          <span>Divisor</span>
          <select value={divisorId} onChange={(event) => setDivisorId(event.target.value)}>
            {divisors.map((item) => (
              <option key={item.id} value={item.id}>
                {item.label}
              </option>
            ))}
          </select>
        </label>
        <label>
          <span>Length</span>
          <input type="number" value={length} onChange={(event) => setLength(event.target.value)} />
        </label>
        <label>
          <span>Width</span>
          <input type="number" value={width} onChange={(event) => setWidth(event.target.value)} />
        </label>
        <label>
          <span>Height</span>
          <input type="number" value={height} onChange={(event) => setHeight(event.target.value)} />
        </label>
      </div>
      <StatGrid
        items={[
          { label: "Dimensional weight", value: formatMeasurement(dimensional, system === "imperial" ? "lb" : "kg") },
          { label: "Actual weight", value: formatMeasurement(actual, system === "imperial" ? "lb" : "kg") },
          { label: "Billable weight", value: `${formatMeasurement(billableLb, "lb")} / ${formatMeasurement(billableKg, "kg")}` },
          { label: "Package volume", value: formatMeasurement(volume, system === "imperial" ? "in3" : "cm3") }
        ]}
      />
    </div>
  );
}

function PaintCoverageConverter({ context }) {
  const [wallLength, setWallLength] = useState("12");
  const [wallHeight, setWallHeight] = useState("8");
  const [walls, setWalls] = useState("4");
  const [unit, setUnit] = useState("ft");
  const [coats, setCoats] = useState("2");
  const [coverage, setCoverage] = useState("350");
  const [doors, setDoors] = useState("1");
  const [windows, setWindows] = useState("2");

  const wallAreaSqm = lengthToMeters(wallLength, unit) * lengthToMeters(wallHeight, unit) * parseNumber(walls);
  const openingSqft = parseNumber(doors) * 20 + parseNumber(windows) * 15;
  const paintAreaSqft = Math.max(wallAreaSqm / 0.09290304 - openingSqft, 0);
  const gallons = paintAreaSqft * parseNumber(coats) / Math.max(parseNumber(coverage, 350), 1);
  const liters = gallons * 3.785411784;

  return (
    <div className="tool-card">
      <ToolHeader context={context} />
      <div className="form-grid four">
        <label>
          <span>Wall length</span>
          <input type="number" value={wallLength} onChange={(event) => setWallLength(event.target.value)} />
        </label>
        <label>
          <span>Wall height</span>
          <input type="number" value={wallHeight} onChange={(event) => setWallHeight(event.target.value)} />
        </label>
        <label>
          <span>Unit</span>
          <select value={unit} onChange={(event) => setUnit(event.target.value)}>
            <option value="ft">feet</option>
            <option value="m">meters</option>
          </select>
        </label>
        <label>
          <span>Walls</span>
          <input type="number" value={walls} onChange={(event) => setWalls(event.target.value)} />
        </label>
        <label>
          <span>Coats</span>
          <input type="number" value={coats} onChange={(event) => setCoats(event.target.value)} />
        </label>
        <label>
          <span>Coverage</span>
          <input type="number" value={coverage} onChange={(event) => setCoverage(event.target.value)} />
        </label>
        <label>
          <span>Doors</span>
          <input type="number" value={doors} onChange={(event) => setDoors(event.target.value)} />
        </label>
        <label>
          <span>Windows</span>
          <input type="number" value={windows} onChange={(event) => setWindows(event.target.value)} />
        </label>
      </div>
      <StatGrid
        items={[
          { label: "Paintable area", value: formatMeasurement(paintAreaSqft, "sq ft") },
          { label: "Gallons", value: formatMeasurement(gallons, "gal") },
          { label: "Liters", value: formatMeasurement(liters, "L") },
          { label: "Rounded buy amount", value: `${Math.ceil(gallons)} gal / ${Math.ceil(liters)} L` }
        ]}
      />
    </div>
  );
}

function PoolVolumeConverter({ context }) {
  const [length, setLength] = useState("8");
  const [width, setWidth] = useState("4");
  const [depth, setDepth] = useState("1.5");
  const [unit, setUnit] = useState("m");
  const volumeM3 = lengthToMeters(length, unit) * lengthToMeters(width, unit) * lengthToMeters(depth, unit);
  const liters = volumeM3 * 1000;
  const gallons = liters / 3.785411784;

  return (
    <div className="tool-card">
      <ToolHeader context={context} />
      <div className="form-grid four">
        <label>
          <span>Length</span>
          <input type="number" value={length} onChange={(event) => setLength(event.target.value)} />
        </label>
        <label>
          <span>Width</span>
          <input type="number" value={width} onChange={(event) => setWidth(event.target.value)} />
        </label>
        <label>
          <span>Average depth</span>
          <input type="number" value={depth} onChange={(event) => setDepth(event.target.value)} />
        </label>
        <label>
          <span>Unit</span>
          <select value={unit} onChange={(event) => setUnit(event.target.value)}>
            <option value="m">meters</option>
            <option value="ft">feet</option>
          </select>
        </label>
      </div>
      <StatGrid
        items={[
          { label: "Cubic meters", value: formatMeasurement(volumeM3, "cu m") },
          { label: "Liters", value: formatMeasurement(liters, "L") },
          { label: "US gallons", value: formatMeasurement(gallons, "gal") },
          { label: "Imperial gallons", value: formatMeasurement(liters / 4.54609, "imp gal") }
        ]}
      />
    </div>
  );
}

function GenericContextConverter({ context }) {
  const group = getGroup(context.group);
  const [amount, setAmount] = useState(context.input);
  const [fromUnit, setFromUnit] = useState(context.from);
  const [toUnit, setToUnit] = useState(context.to);
  const output = convertValue(context.group, fromUnit, toUnit, amount);
  const allOutputs = group.units
    .filter((unit) => unit.id !== fromUnit)
    .slice(0, 4)
    .map((unit) => ({
      label: unit.label,
      value: formatMeasurement(convertValue(context.group, fromUnit, unit.id, amount), unit.short)
    }));

  return (
    <div className="tool-card">
      <ToolHeader context={context} />
      <div className="form-grid four">
        <label>
          <span>Amount</span>
          <input type="number" value={amount} onChange={(event) => setAmount(event.target.value)} />
        </label>
        <label>
          <span>From</span>
          <select value={fromUnit} onChange={(event) => setFromUnit(event.target.value)}>
            {group.units.map((unit) => (
              <option key={unit.id} value={unit.id}>
                {unit.label}
              </option>
            ))}
          </select>
        </label>
        <label>
          <span>To</span>
          <select value={toUnit} onChange={(event) => setToUnit(event.target.value)}>
            {group.units.map((unit) => (
              <option key={unit.id} value={unit.id}>
                {unit.label}
              </option>
            ))}
          </select>
        </label>
      </div>
      <StatGrid
        items={[
          { label: "Result", value: formatMeasurement(output, getUnit(group, toUnit).short) },
          ...allOutputs
        ]}
      />
    </div>
  );
}

function toGrams(amount, unit, density) {
  if (unit.type === "mass") {
    return amount * unit.grams;
  }
  return amount * unit.ml * density;
}

function fromGrams(grams, unit, density) {
  if (unit.type === "mass") {
    return grams / unit.grams;
  }
  return grams / density / unit.ml;
}

export default App;
